import React from 'react';

import {View, StyleSheet, FlatList} from 'react-native';

import {colors} from '../../classes/Colors';
import ImageNavCardLayoutHeader from './ImageNavCardLayoutHeader';
import ImageNavCard from './ImageNavCard';
import MaxWidthContainer from '../MaxWidthContainer';
import HeroNavCard from './ImageHeroNavCard';
import useIsWideDevice from '../../hooks/useIsWideDevice';

type Data = {
  id: string,
  onPress: () => {},
  getImage: () => string,
  title: string,
  subtitle: string,
  trust: string,
  displayDate: string,
  status: boolean,
  statusText: string,
};

/**
 * A layout component that renders a list of image navigation cards.
 *
 * @param {Data[]} data - The data array containing items to display.
 * @param {string} title - The title to display in the header.
 *
 * The component determines whether to render a "hero" card or regular
 * image navigation cards based on the index of the items. It adapts
 * the layout for tablet devices, displaying two columns when applicable.
 *
 * - The first item in the `data` array is rendered with a special layout
 *   as a "hero" card.
 * - The remaining items are rendered in a grid or list layout depending
 *   on the device type (tablet or not).
 * - The `renderItem` function handles the logic for styling and displaying
 *   each item.
 */

const ImageNavCardLayout = ({data, title, footer, header}) => {
  const firstItem = data.slice(0, 1)[0];
  const restData = data.slice(1);
  const isTablet = useIsWideDevice();
  const fallbackImage = require('../../assets/Image_Loading_Placeholder_Large.png');

  const renderItem = (item, index, isHero) => {
    const isEven = index % 2 === 0;
    const isOneOfLastTwo =
      index === restData.length - 1 || index === restData.length - 2;
    const isNotFirstTwo = index !== 0 && index !== 1;
    const isLast = index === restData.length - 1;

    if (isHero) {
      return (
        <View style={{marginBottom: 20}}>
          <ImageNavCardLayoutHeader title={title} />
          <HeroNavCard key={item.id} {...item} fallbackImage={fallbackImage} />
        </View>
      );
    }

    if (!isTablet) {
      return (
        <View
          style={[
            {
              paddingVertical: 16,
            },
            !isLast && {
              borderBottomWidth: 1,
              borderBottomColor: colors.Grey100,
            },
          ]}>
          <ImageNavCard key={item.id} {...item} fallbackImage={fallbackImage} />
        </View>
      );
    }

    return (
      <View
        style={[
          // important: When odd items, prevents the last item from going full width.
          {flex: 1 / 2},
          isEven && {
            borderRightWidth: 1,
            borderRightColor: colors.Grey100,
            paddingRight: 36,
          },
          !isEven && {
            paddingLeft: 36,
          },
          isNotFirstTwo && {
            paddingTop: 16,
          },
          isOneOfLastTwo &&
            isLast && {
              paddingRight: 0,
            },
        ]}>
        <ImageNavCard
          key={item.id}
          {...item}
          borderBottom={!isOneOfLastTwo}
          fallbackImage={fallbackImage}
        />
      </View>
    );
  };

  const numberOfColumns = isTablet ? 2 : 1;

  const headerComponent = (
    <>
      {header}
      {firstItem && renderItem(firstItem, 0, true)}
    </>
  );
  return (
    <FlatList
      // key forces re-render on screen size change
      key={numberOfColumns}
      data={restData}
      ListHeaderComponent={headerComponent}
      renderItem={({item, index}) => renderItem(item, index, false)}
      keyExtractor={item => item.id}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.flatListContainer}
      numColumns={numberOfColumns}
      ListFooterComponent={footer ?? <View style={{marginBottom: 20}}></View>}
    />
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.white,
  },
  flatListContainer: {
    flexGrow: 1,
    alignSelf: 'stretch',
  },
});

export default ImageNavCardLayout;
